/* eslint-env browser */
// Settings › Ringtones (wave 3, W2 look): the default sound for all drivers, and
// the clips a driver can pick for the incoming-booking alert (Profile › Incoming
// booking sound). Admin only: the Settings page is admin-only and the RTDB
// /ringtones and /ringtone_default nodes are admin-write; the Storage object is
// create-only (removing a clip deletes the row, the audio stays so drivers who
// picked it can re-download it). Reads are one-shot (on open, after each change,
// and on Refresh); nothing here is a listener. Data contract:
// common/src/actions/ringtoneactions.js and DRIVER_RINGTONES.md.
// Times on screen are Addis Ababa time on a 12-hour clock.
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import UploadFileRoundedIcon from '@mui/icons-material/UploadFileRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import StopRoundedIcon from '@mui/icons-material/StopRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import RestartAltRoundedIcon from '@mui/icons-material/RestartAltRounded';
import MusicNoteOutlinedIcon from '@mui/icons-material/MusicNoteOutlined';
import { api } from 'common';
import { confirmDialog, showToast, InlineMessage } from '../components/feedback';
import { tokens as c } from '../theme';
import { formatAddisDateTime } from '../common/exportUtils';
import { DataTable, FieldLabel, PageHeader, IconCircle, StatusChip } from '../components/modern';

const kb = (bytes) => Math.max(1, Math.round((Number(bytes) || 0) / 1024)) + ' KB';
const publicUrl = (file) => (process.env.PUBLIC_URL || '') + '/sounds/' + file;

// Decode the WHOLE clip in the browser before uploading (Web Audio): a file
// the browser cannot decode never reaches the drivers, and the length is
// exact — <audio> metadata only estimates it for VBR aac / mp3. Resolves the
// length in seconds; falls back to the metadata probe without Web Audio.
const decodeAudio = async (file) => {
  const Ctx = window.AudioContext || window.webkitAudioContext;
  if (!Ctx) return probeAudio(file);
  const ctx = new Ctx();
  try {
    const buf = await file.arrayBuffer();
    const audio = await new Promise((resolve, reject) => {
      const p = ctx.decodeAudioData(buf, resolve, reject);
      if (p && typeof p.then === 'function') p.then(resolve, reject);
    });
    return audio.duration;
  } catch (e) {
    throw new Error('This file does not play — export it again as mp3.');
  } finally {
    try { ctx.close(); } catch (e) { /* noop */ }
  }
};

const probeAudio = (file) => new Promise((resolve, reject) => {
  const url = URL.createObjectURL(file);
  const a = new Audio();
  let settled = false;
  let timer = null;
  const finish = (ok, value) => {
    if (settled) return;
    settled = true;
    clearTimeout(timer);
    a.onloadedmetadata = null;
    a.onerror = null;
    a.removeAttribute('src');
    try { a.load(); } catch (e) { /* noop */ }
    URL.revokeObjectURL(url);
    if (ok) resolve(value); else reject(value);
  };
  timer = setTimeout(() => finish(false, new Error('The browser could not read this file in time — export it again as mp3.')), 8000);
  a.preload = 'metadata';
  a.onloadedmetadata = () => finish(true, a.duration);
  a.onerror = () => finish(false, new Error('This file does not play — export it again as mp3.'));
  a.src = url;
});

export default function RingtoneSettings() {
  const { t, i18n } = useTranslation();
  const auth = useSelector((state) => state.auth);
  const [list, setList] = useState(null); // null = loading
  const [officeRow, setOfficeRow] = useState(null); // /ringtone_default; null = never set, Classic
  const [error, setError] = useState(null);
  const [name, setName] = useState('');
  const [file, setFile] = useState(null);
  const [markDefault, setMarkDefault] = useState(false);
  const [markEveryone, setMarkEveryone] = useState(false);
  const [busy, setBusy] = useState(false);
  const [playing, setPlaying] = useState(null);
  const [asking, setAsking] = useState(null); // the sound the "make default" dialog is about
  const [askEveryone, setAskEveryone] = useState('new');
  const audioRef = useRef(null);
  const inputRef = useRef(null);
  const who = auth.profile && auth.profile.uid;

  const builtIns = [
    { id: api.RINGTONE_CLASSIC_ID, builtIn: true, name: t('ringtones_classic', { defaultValue: 'Classic' }), note: t('ringtones_classic_note', { defaultValue: 'The sound the app came with' }), url: publicUrl('classic.m4a') },
    { id: api.RINGTONE_NEW_ORDER_1_ID, builtIn: true, name: t('ringtones_new_order_1', { defaultValue: 'Digital ring' }), note: t('ringtones_new_order_1_note', { defaultValue: 'A steady electronic ring' }), url: publicUrl('new_order_1.m4a') },
    { id: api.RINGTONE_NEW_ORDER_2_ID, builtIn: true, name: t('ringtones_new_order_2', { defaultValue: 'Old phone' }), note: t('ringtones_new_order_2_note', { defaultValue: 'Like an old desk phone' }), url: publicUrl('new_order_2.m4a') },
    { id: api.RINGTONE_NEW_ORDER_3_ID, builtIn: true, name: t('ringtones_new_order_3', { defaultValue: 'Quick chimes' }), note: t('ringtones_new_order_3_note', { defaultValue: 'Light and lively' }), url: publicUrl('new_order_3.m4a') },
    { id: api.RINGTONE_NEW_ORDER_4_ID, builtIn: true, name: t('ringtones_new_order_4', { defaultValue: 'Alarm' }), note: t('ringtones_new_order_4_note', { defaultValue: 'Loud and urgent' }), url: publicUrl('new_order_4.m4a') },
    { id: api.RINGTONE_UPLIFTING_BELLS_ID, builtIn: true, name: t('ringtones_uplifting_bells', { defaultValue: 'Uplifting bells' }), note: t('ringtones_uplifting_bells_note', { defaultValue: 'Bright and cheerful' }), url: publicUrl('uplifting_bells_loop.m4a') },
  ];
  const clips = Array.isArray(list) ? list.slice().reverse() : []; // newest first
  const rows = builtIns.concat(clips);
  const officeId = officeRow ? officeRow.id : api.RINGTONE_CLASSIC_ID;
  // Always something to show, even when the clip behind it left the list.
  const current = rows.find((r) => r.id === officeId) || (officeRow && officeRow.url ? officeRow : builtIns[0]);

  const stop = useCallback(() => {
    if (audioRef.current) { try { audioRef.current.pause(); } catch (e) { /* noop */ } audioRef.current = null; }
    setPlaying(null);
  }, []);
  const load = useCallback(async () => {
    const [r, d] = await Promise.all([api.fetchRingtones(), api.fetchRingtoneDefault()]);
    if (r.success) { setList(r.list); } else { setList([]); }
    if (d.success) setOfficeRow(d.row);
    setError(r.success ? (d.success ? null : d.error) : r.error);
  }, []);
  useEffect(() => { load(); return stop; }, [load, stop]);

  const play = (row) => {
    if (playing === row.id) { stop(); return; }
    stop();
    const a = new Audio(row.url);
    audioRef.current = a;
    // Every handler checks it still belongs to the current clip: pausing a
    // clip that was still loading rejects its play() later, and that must not
    // reset the clip that is playing now.
    const mine = () => audioRef.current === a;
    a.onended = () => { if (mine()) setPlaying(null); };
    a.onerror = () => { if (!mine()) return; setPlaying(null); showToast.error(t('ringtones_play_failed', { defaultValue: 'Could not play this file in the browser.' })); };
    setPlaying(row.id);
    a.play().catch(() => { if (mine()) setPlaying(null); });
  };
  const playButton = (row) => {
    const isPlaying = playing === row.id;
    const label = isPlaying ? t('ringtones_stop', { defaultValue: 'Stop' }) : t('ringtones_play', { defaultValue: 'Play' });
    return (
      <Tooltip title={label}>
        <IconButton
          onClick={() => play(row)}
          aria-label={`${label}: ${row.name}`}
          aria-pressed={isPlaying ? 'true' : 'false'}
          sx={{ color: c.ACCENT_TEXT, bgcolor: c.ACCENT_TINT, '&:hover': { bgcolor: c.ACCENT_TINT }, flex: 'none' }}
        >
          {isPlaying ? <StopRoundedIcon /> : <PlayArrowRoundedIcon />}
        </IconButton>
      </Tooltip>
    );
  };

  const saveDefault = async (row, everyone) => {
    setBusy(true);
    try {
      await api.setRingtoneDefault(row, { override: everyone, who });
      showToast.success(everyone
        ? t('ringtones_default_saved_all', { name: row.name, defaultValue: 'Every driver is moving to "{{name}}".' })
        : t('ringtones_default_saved', { name: row.name, defaultValue: '"{{name}}" is now the default.' }));
      await load();
    } catch (err) {
      showToast.error(err.message);
    } finally {
      setBusy(false);
    }
  };
  const askDefault = (row) => {
    setAskEveryone('new');
    setAsking(row);
  };
  const answerDefault = () => {
    const row = asking;
    setAsking(null);
    saveDefault(row, askEveryone === 'all');
  };
  // Everyone back on the current default, including drivers who picked their own.
  const everyoneOnCurrent = async () => {
    const ok = await confirmDialog({
      title: t('ringtones_everyone_title', { name: current.name, defaultValue: 'Put every driver on "{{name}}"?' }),
      body: t('ringtones_everyone_body', { defaultValue: 'This replaces the sound drivers picked themselves. They can pick another in Profile afterwards.' }),
      confirmLabel: t('ringtones_everyone_confirm', { defaultValue: 'Put everyone on it' }),
    });
    if (ok) saveDefault(current, true);
  };

  const upload = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const seconds = await decodeAudio(file);
      if (!Number.isFinite(seconds) || seconds <= 0) throw new Error(t('ringtones_no_length', { defaultValue: 'Could not read the length of this clip — export it again as mp3.' }));
      if (seconds > api.RINGTONE_MAX_SECONDS) throw new Error(t('ringtones_too_long', { max: api.RINGTONE_MAX_SECONDS, defaultValue: 'Keep the clip under {{max}} seconds — it loops while a request waits.' }));
      const row = await api.uploadRingtone(name, file, who, seconds);
      if (markDefault) {
        await api.setRingtoneDefault(row, { override: markEveryone, who });
        showToast.success(markEveryone
          ? t('ringtones_default_saved_all', { name: row.name, defaultValue: 'Every driver is moving to "{{name}}".' })
          : t('ringtones_default_saved', { name: row.name, defaultValue: '"{{name}}" is now the default.' }));
      } else {
        showToast.success(t('ringtones_uploaded', { name: row.name, defaultValue: '"{{name}}" is now offered to drivers.' }));
      }
      setName(''); setFile(null); setMarkDefault(false); setMarkEveryone(false);
      if (inputRef.current) inputRef.current.value = '';
      await load();
    } catch (err) {
      showToast.error(err.message);
    } finally {
      setBusy(false);
    }
  };
  const remove = async (row) => {
    // Phones would keep ringing a sound nobody can see here.
    if (row.id === officeId) {
      showToast.error(t('ringtones_default_cannot_remove', { name: row.name, defaultValue: '"{{name}}" is the default for all drivers. Make another sound the default first.' }));
      return;
    }
    const ok = await confirmDialog({
      title: t('ringtones_confirm_delete', { name: row.name, defaultValue: 'Remove "{{name}}"? Drivers who already picked it keep their saved copy; nobody new can choose it.' }),
      confirmLabel: t('ringtones_delete', { defaultValue: 'Remove' }),
      destructive: true,
    });
    if (!ok) return;
    stop();
    try { await api.deleteRingtone(row.id); showToast.success(t('ringtones_deleted', { defaultValue: 'Removed.' })); await load(); }
    catch (err) { showToast.error(err.message); }
  };

  const full = Array.isArray(list) && list.length >= api.RINGTONE_MAX_COUNT;
  const maxKb = Math.round(api.RINGTONE_MAX_BYTES / 1024);
  const title = t('ringtones_title', { defaultValue: 'Driver ringtones' });

  const columns = [
    {
      title: t('ringtones_name', { defaultValue: 'Name drivers will see' }),
      field: 'name',
      sortable: false,
      render: (row) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
          {playButton(row)}
          <Box sx={{ minWidth: 0 }}>
            <Box component="span" sx={{ fontWeight: 600, overflowWrap: 'anywhere' }}>{row.name}</Box>
            {row.note ? <Box sx={{ color: c.INK_2, fontSize: 13 }}>{row.note}</Box> : null}
          </Box>
        </Box>
      ),
    },
    {
      title: t('ringtones_default_col', { defaultValue: 'Default' }),
      field: 'id',
      sortable: false,
      render: (row) => (row.id === officeId ? (
        <StatusChip tone="success" icon={<CheckRoundedIcon />} label={t('ringtones_is_default', { defaultValue: 'Default for all drivers' })} />
      ) : (
        <Button size="small" variant="outlined" color="neutral" disabled={busy} onClick={() => askDefault(row)} sx={{ whiteSpace: 'nowrap' }}>
          {t('ringtones_make_default', { defaultValue: 'Make default' })}
        </Button>
      )),
    },
    {
      title: t('ringtones_file', { defaultValue: 'File' }),
      field: 'ext',
      sortable: false,
      render: (row) => (
        <Box component="span" sx={{ color: c.INK_2, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
          {row.builtIn
            ? t('ringtones_built_in', { defaultValue: 'Comes with the app' })
            : (row.ext || t('ringtones_audio_file', { defaultValue: 'audio' })) + ' · ' + kb(row.bytes) + (row.sec ? ' · ' + row.sec + ' s' : '')}
        </Box>
      ),
    },
    {
      title: t('ringtones_added', { defaultValue: 'Added' }),
      field: 'at',
      type: 'datetime',
      sortable: false,
      render: (row) => (row.at ? <Box component="span" sx={{ whiteSpace: 'nowrap' }}>{formatAddisDateTime(row.at, { weekday: true })}</Box> : null),
    },
  ];

  const actions = [
    {
      key: 'remove',
      label: t('ringtones_delete', { defaultValue: 'Remove' }),
      icon: <DeleteOutlineRoundedIcon />,
      inline: true,
      destructive: true,
      show: (row) => !row.builtIn,
      onClick: remove,
    },
  ];

  return (
    <Box dir={i18n.dir()} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <PageHeader
        title={title}
        icon={<MusicNoteOutlinedIcon />}
        hue="accent"
        secondaryActions={[{ key: 'refresh', label: t('refresh', { defaultValue: 'Refresh' }), icon: <RefreshRoundedIcon />, onClick: load, disabled: busy }]}
      />
      {error ? (
        <InlineMessage kind="error" actionLabel={t('retry', { defaultValue: 'Retry' })} onAction={load}>
          {error}
        </InlineMessage>
      ) : null}

      <Paper sx={{ p: { xs: 2, md: 2.5 }, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.5 }}>
          <IconCircle icon={<NotificationsActiveOutlinedIcon />} hue="accent" size={36} sx={{ display: { xs: 'none', sm: 'inline-flex' } }} />
          <Box sx={{ flex: '1 1 240px', minWidth: 0 }}>
            <Typography variant="body2" sx={{ color: c.TEXT_3 }}>
              {t('ringtones_default_title', { defaultValue: 'Default sound for all drivers' })}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.25 }}>
              <Typography sx={{ fontWeight: 700, fontSize: 18, overflowWrap: 'anywhere' }}>{current.name}</Typography>
              {playButton(current)}
            </Box>
          </Box>
          <Button variant="outlined" color="neutral" startIcon={<GroupsOutlinedIcon />} disabled={busy || list === null} onClick={everyoneOnCurrent}>
            {t('ringtones_put_everyone', { defaultValue: 'Put every driver on this sound' })}
          </Button>
          {officeId !== api.RINGTONE_CLASSIC_ID ? (
            <Button variant="outlined" color="neutral" startIcon={<RestartAltRoundedIcon />} disabled={busy} onClick={() => askDefault(builtIns[0])}>
              {t('ringtones_reset_classic', { defaultValue: 'Reset to Classic' })}
            </Button>
          ) : null}
        </Box>
        <Typography variant="body2" sx={{ color: c.TEXT_3 }}>
          {t('ringtones_default_hint', {
            defaultValue:
              'Drivers who have not picked their own sound hear this. Phones get a change the next time the app opens, or within an hour if it stays open. It plays while the app is open; with the app closed or in the background the phone keeps the usual notification sound.',
          })}
        </Typography>
      </Paper>

      <Paper sx={{ p: { xs: 2, md: 2.5 }, display: 'flex', alignItems: 'flex-end', flexWrap: 'wrap', gap: 2 }}>
        <IconCircle icon={<UploadFileRoundedIcon />} hue="accent" size={36} sx={{ mb: 0.5, display: { xs: 'none', sm: 'inline-flex' } }} />
        <Box sx={{ flex: '1 1 260px', minWidth: 0, maxWidth: 360 }}>
          <FieldLabel htmlFor="ringtone-name">{t('ringtones_name', { defaultValue: 'Name drivers will see' })}</FieldLabel>
          <TextField
            id="ringtone-name"
            size="small"
            fullWidth
            value={name}
            onChange={(e) => setName(e.target.value.slice(0, api.RINGTONE_NAME_MAX))}
            disabled={busy || full}
          />
        </Box>
        <Button component="label" variant="outlined" color="neutral" startIcon={<UploadFileRoundedIcon />} disabled={busy || full} sx={{ maxWidth: '100%' }}>
          <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 260 }}>
            {file ? file.name + ' · ' + kb(file.size) : t('ringtones_choose_file', { defaultValue: 'Choose audio file' })}
          </Box>
          <input
            ref={inputRef}
            hidden
            type="file"
            accept={api.RINGTONE_ACCEPT}
            onChange={(e) => {
              const f = e.target.files && e.target.files[0];
              setFile(f || null);
              if (f && !name) setName(f.name.replace(/\.[a-z0-9]{2,4}$/i, '').slice(0, api.RINGTONE_NAME_MAX));
            }}
          />
        </Button>
        <Button
          variant="contained"
          onClick={upload}
          disabled={busy || full || !file || !name.trim()}
          startIcon={busy ? <CircularProgress size={16} color="inherit" /> : <AddRoundedIcon />}
        >
          {busy ? t('ringtones_uploading', { defaultValue: 'Checking and uploading…' }) : t('ringtones_upload', { defaultValue: 'Add ringtone' })}
        </Button>
        <Box sx={{ flexBasis: '100%', display: 'flex', flexWrap: 'wrap', columnGap: 3 }}>
          <FormControlLabel
            control={<Checkbox checked={markDefault} disabled={busy || full} onChange={(e) => { setMarkDefault(e.target.checked); if (!e.target.checked) setMarkEveryone(false); }} />}
            label={t('ringtones_mark_default', { defaultValue: 'Make it the default for all drivers' })}
          />
          {markDefault ? (
            <FormControlLabel
              control={<Checkbox checked={markEveryone} disabled={busy} onChange={(e) => setMarkEveryone(e.target.checked)} />}
              label={t('ringtones_mark_everyone', { defaultValue: 'Also replace the sound drivers picked' })}
            />
          ) : null}
        </Box>
        {full ? (
          <Typography variant="body2" sx={{ color: c.WARNING_TEXT, flexBasis: '100%' }}>
            {t('ringtones_max', { max: api.RINGTONE_MAX_COUNT, defaultValue: 'The list is full ({{max}} clips) — remove one to add another.' })}
          </Typography>
        ) : null}
        <Typography variant="body2" sx={{ color: c.TEXT_3, flexBasis: '100%' }}>
          {t('ringtones_hint2', {
            kb: maxKb,
            max: api.RINGTONE_MAX_COUNT,
            sec: api.RINGTONE_MAX_SECONDS,
            defaultValue:
              'Short clips drivers can choose in Profile › Incoming booking sound. The phone downloads a clip once and loops it while a booking request waits. Keep clips 5–20 seconds (at most {{sec}}) and under {{kb}} KB — mp3, wav, m4a or aac — at most {{max}} clips. Every file is test-played in this browser before it is uploaded.',
          })}
        </Typography>
      </Paper>

      <DataTable
        title={title}
        rows={rows}
        columns={columns}
        loading={list === null}
        search={false}
        paging={false}
        densityToggle={false}
        columnsMenu={false}
        countText={({ loading }) =>
          loading
            ? t('loading', { defaultValue: 'Loading…' })
            : t('ringtones_count', { count: clips.length, max: api.RINGTONE_MAX_COUNT, defaultValue_one: '{{count}} of {{max}} clips', defaultValue_other: '{{count}} of {{max}} clips', defaultValue: '{{count}} of {{max}} clips' })
        }
        rowLabel={(row) => row.name}
        actions={actions}
        minWidth={720}
        maxHeight={null}
      />

      <Dialog open={!!asking} onClose={() => setAsking(null)} maxWidth="xs" fullWidth aria-labelledby="ringtone-default-title">
        <DialogTitle id="ringtone-default-title">
          {asking ? t('ringtones_dialog_title', { name: asking.name, defaultValue: 'Make "{{name}}" the default?' }) : ''}
        </DialogTitle>
        <DialogContent>
          <RadioGroup value={askEveryone} onChange={(e) => setAskEveryone(e.target.value)} aria-labelledby="ringtone-default-title">
            <FormControlLabel
              value="new"
              control={<Radio />}
              sx={{ alignItems: 'flex-start', mb: 1.5 }}
              label={(
                <Box sx={{ pt: 1 }}>
                  <Typography sx={{ fontWeight: 600 }}>{t('ringtones_scope_new', { defaultValue: 'Only drivers who have not picked a sound' })}</Typography>
                  <Typography variant="body2" sx={{ color: c.TEXT_3 }}>{t('ringtones_scope_new_sub', { defaultValue: 'Drivers who picked their own sound keep it.' })}</Typography>
                </Box>
              )}
            />
            <FormControlLabel
              value="all"
              control={<Radio />}
              sx={{ alignItems: 'flex-start' }}
              label={(
                <Box sx={{ pt: 1 }}>
                  <Typography sx={{ fontWeight: 600 }}>{t('ringtones_scope_all', { defaultValue: 'Every driver' })}</Typography>
                  <Typography variant="body2" sx={{ color: c.TEXT_3 }}>{t('ringtones_scope_all_sub', { defaultValue: 'Also replaces the sound drivers picked. They can pick another in Profile afterwards.' })}</Typography>
                </Box>
              )}
            />
          </RadioGroup>
        </DialogContent>
        <DialogActions>
          <Button color="neutral" onClick={() => setAsking(null)}>{t('cancel', { defaultValue: 'Cancel' })}</Button>
          <Button variant="contained" onClick={answerDefault}>{t('ringtones_make_default', { defaultValue: 'Make default' })}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
